const VIEWPORT_MARGIN = 12;
const DEFAULT_TOOLTIP_WIDTH = 260;

export function computeFixedTooltipStyle(rect, {
  align = 'center',
  offset = 8,
  zIndex = 10000,
  tooltipWidth = DEFAULT_TOOLTIP_WIDTH,
  estimatedHeight = 80,
} = {}) {
  if (!rect) {
    return { position: 'fixed', zIndex };
  }

  const style = {
    position: 'fixed',
    zIndex,
  };

  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const triggerCenterX = rect.left + (rect.width / 2);

  const spaceBelow = viewportHeight - rect.bottom - offset;
  const showAbove = spaceBelow < estimatedHeight
    && rect.top - offset - estimatedHeight > VIEWPORT_MARGIN;

  if (showAbove) {
    style.top = `${rect.top - offset}px`;
    style.transform = 'translateY(-100%)';
  } else {
    style.top = `${rect.bottom + offset}px`;
  }

  let left;
  if (align === 'center') {
    left = triggerCenterX - (tooltipWidth / 2);
  } else {
    left = rect.right - tooltipWidth;
  }

  left = Math.max(
    VIEWPORT_MARGIN,
    Math.min(left, viewportWidth - tooltipWidth - VIEWPORT_MARGIN),
  );

  style.left = `${left}px`;

  const arrowLeft = Math.max(
    12,
    Math.min(tooltipWidth - 12, triggerCenterX - left - 6),
  );
  const arrowRight = Math.max(
    8,
    Math.min(tooltipWidth - 8, (left + tooltipWidth) - triggerCenterX - 6),
  );

  style['--tooltip-arrow-left'] = `${arrowLeft}px`;
  style['--tooltip-arrow-right'] = `${arrowRight}px`;

  return style;
}

function isScrollableOverflow(value) {
  return value === 'auto' || value === 'scroll' || value === 'overlay';
}

export function bindTooltipScrollFollow(anchorEl, callback) {
  if (!anchorEl || typeof callback !== 'function') {
    return () => {};
  }

  let frame = null;
  const cleanups = [];
  let cancelled = false;

  const schedule = () => {
    if (frame) return;
    frame = requestAnimationFrame(() => {
      frame = null;
      callback();
    });
  };

  const addListener = (target, event, options) => {
    if (!target?.addEventListener) return;
    target.addEventListener(event, schedule, options);
    cleanups.push(() => target.removeEventListener(event, schedule, options));
  };

  addListener(window, 'resize');
  addListener(window, 'scroll', { capture: true, passive: true });

  let node = anchorEl.parentElement;
  while (node && node !== document.body) {
    const style = window.getComputedStyle(node);
    if (
      isScrollableOverflow(style.overflowY)
      || isScrollableOverflow(style.overflowX)
    ) {
      addListener(node, 'scroll', { passive: true });
    }
    node = node.parentElement;
  }

  const ionContent = anchorEl.closest('ion-content');
  if (ionContent?.getScrollElement) {
    ionContent.getScrollElement()
      .then((scrollEl) => {
        if (cancelled || !scrollEl) return;
        addListener(scrollEl, 'scroll', { passive: true });
      })
      .catch(() => {});
  }

  return () => {
    cancelled = true;
    if (frame) {
      cancelAnimationFrame(frame);
      frame = null;
    }
    cleanups.forEach((cleanup) => cleanup());
  };
}

export function isInsideTooltipInteraction(event, {
  wrapperSelector,
  tooltipSelector,
  triggerSelector = '.info-icon-btn, .info-btn',
} = {}) {
  const path = event.composedPath?.() ?? [event.target];
  return path.some((el) => {
    if (!el?.classList) return false;
    if (triggerSelector) {
      const selectors = triggerSelector.split(',').map((s) => s.trim());
      if (selectors.some((sel) => el.matches?.(sel))) return true;
    }
    if (wrapperSelector && el.matches?.(wrapperSelector)) return true;
    if (tooltipSelector && el.matches?.(tooltipSelector)) return true;
    if (el.classList.contains('editor-info-tooltip')) return true;
    return false;
  });
}
